/**
 * @description Admin portal page for configuring the platform email provider
 *
 * Key features:
 * - Select email provider (Resend — Phase 1; SendGrid, Postmark listed as future)
 * - Enter/update API key (encrypted at rest, masked in UI)
 * - Default from name, merchant From, and CP-unverified From (factory)
 * - Enable/disable email sending platform-wide
 * - Test connection button (validates key against provider API)
 * - Status indicator (configured / not configured / error)
 *
 * @dependencies
 * - adminApi: getEmailConfig, updateEmailConfig, testEmailConnection
 *
 * @notes
 * - API key stored encrypted via EncryptionService in platform_config.email_config
 * - Only last 4 chars of API key shown after save (masked in response)
 * - Provider dropdown shows future options disabled (only Resend active)
 * - Follows admin portal page patterns (PlatformSettings style)
 */

import { useState, useEffect, useCallback } from 'react';
import { Mail, Shield, CheckCircle, XCircle, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { getEmailConfig, updateEmailConfig, testEmailConnection } from '../../services/api/adminApi';

const PROVIDERS = [
  { value: 'resend', label: 'Resend', available: true },
  { value: 'sendgrid', label: 'SendGrid', available: false },
  { value: 'postmark', label: 'Postmark', available: false }
];

function EmailProviderConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [provider, setProvider] = useState('resend');
  const [apiKey, setApiKey] = useState('');
  const [maskedKey, setMaskedKey] = useState('');
  const [defaultFromName, setDefaultFromName] = useState('');
  const [defaultFromEmail, setDefaultFromEmail] = useState('notifications@lynxpay.us');
  const [channelPartnerUnverifiedFromEmail, setChannelPartnerUnverifiedFromEmail] = useState('notifications@lynx.build');
  const [enabled, setEnabled] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Status
  const [isConfigured, setIsConfigured] = useState(false);
  const [testResult, setTestResult] = useState(null); // { valid, message }

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getEmailConfig();
      const config = result.data || {};

      setProvider(config.provider || 'resend');
      setMaskedKey(config.apiKey || '');
      setDefaultFromName(config.defaultFromName || '');
      setDefaultFromEmail(config.defaultFromEmail || 'notifications@lynxpay.us');
      setChannelPartnerUnverifiedFromEmail(
        config.channelPartnerUnverifiedFromEmail || 'notifications@lynx.build'
      );
      setEnabled(config.enabled ?? false);
      setIsConfigured(config.configured ?? false);
      setApiKey(''); // Never pre-fill actual key
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error loading email config:', err);
        setError('Failed to load email configuration');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    if (saving) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      setTestResult(null);

      const payload = {
        provider,
        defaultFromName,
        defaultFromEmail,
        channelPartnerUnverifiedFromEmail,
        enabled,
        ...(apiKey.trim() && { apiKey: apiKey.trim() })
      };

      await updateEmailConfig(payload);
      setSuccess('Email configuration saved');
      setApiKey('');
      setTimeout(() => setSuccess(''), 5000);
      await loadConfig();
    } catch (err) {
      console.error('Error saving email config:', err);
      setError(err.response?.data?.error || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (testing) return;

    try {
      setTesting(true);
      setError('');
      setTestResult(null);

      const result = await testEmailConnection();
      setTestResult({
        valid: result.data?.valid ?? false,
        message: result.data?.message || (result.data?.valid ? 'Connection successful' : 'Connection failed')
      });
    } catch (err) {
      console.error('Error testing email connection:', err);
      setTestResult({
        valid: false,
        message: err.response?.data?.error || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          <p className="text-sm text-gray-500">Loading email configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Mail className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Email Provider</h1>
            <p className="text-sm text-gray-500">Configure the platform email sending service</p>
          </div>
        </div>

        {/* Status indicator */}
        <div className="mt-4">
          {isConfigured ? (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">Configured</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-700">Not Configured</span>
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 space-y-6">
          {/* Provider Selection */}
          <div>
            <label htmlFor="email-provider" className="block text-sm font-medium text-gray-900 mb-1">
              Email Provider
            </label>
            <select
              id="email-provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              disabled={saving}
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value} disabled={!p.available}>
                  {p.label}{!p.available ? ' (Coming Soon)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* API Key */}
          <div>
            <label htmlFor="email-api-key" className="block text-sm font-medium text-gray-900 mb-1">
              API Key
            </label>
            {maskedKey && !apiKey && (
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500 font-mono">
                  ****{maskedKey}
                </span>
                <span className="text-xs text-gray-400">(encrypted)</span>
              </div>
            )}
            <div className="relative max-w-md">
              <input
                id="email-api-key"
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={maskedKey ? 'Enter new key to replace' : 'Enter your Resend API key'}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                disabled={saving}
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Default From Name */}
          <div>
            <label htmlFor="email-from-name" className="block text-sm font-medium text-gray-900 mb-1">
              Default From Name
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Fallback display name when merchant has no custom sender name
            </p>
            <input
              id="email-from-name"
              type="text"
              value={defaultFromName}
              onChange={(e) => setDefaultFromName(e.target.value)}
              placeholder="LynxPay"
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              disabled={saving}
            />
          </div>

          {/* Default From Email */}
          <div>
            <label htmlFor="email-from-address" className="block text-sm font-medium text-gray-900 mb-1">
              Default From Email
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Merchant and LynxPay fallback when a sender has no custom From
            </p>
            <input
              id="email-from-address"
              type="email"
              value={defaultFromEmail}
              onChange={(e) => setDefaultFromEmail(e.target.value)}
              placeholder="notifications@lynxpay.us"
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              disabled={saving}
            />
          </div>

          {/* CP unverified From — factory field, not a buried constant */}
          <div>
            <label htmlFor="email-cp-unverified-from" className="block text-sm font-medium text-gray-900 mb-1">
              Channel Partner unverified From Email
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Used for Channel Partner account mail until that partner&apos;s sending
              domain is verified. Resend must have lynx.build verified to deliver.
            </p>
            <input
              id="email-cp-unverified-from"
              type="email"
              value={channelPartnerUnverifiedFromEmail}
              onChange={(e) => setChannelPartnerUnverifiedFromEmail(e.target.value)}
              placeholder="notifications@lynx.build"
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              disabled={saving}
            />
          </div>

          {/* Enable/Disable Toggle */}
          <div>
            <label htmlFor="email-enabled" className="flex items-center gap-3 cursor-pointer">
              <input
                id="email-enabled"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="rounded border-gray-300 text-slate-800 focus:ring-slate-500 w-5 h-5"
                disabled={saving}
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Enable Email Sending</span>
                <p className="text-xs text-gray-500">
                  When disabled, all email sends will be silently skipped
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Test Connection Result */}
        {testResult && (
          <div className={`mx-6 mb-4 p-3 rounded-lg border ${
            testResult.valid
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              {testResult.valid ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              <span className={`text-sm font-medium ${testResult.valid ? 'text-green-700' : 'text-red-700'}`}>
                {testResult.message}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={handleTest}
            disabled={testing || (!isConfigured && !apiKey.trim())}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Testing...
              </>
            ) : (
              'Test Connection'
            )}
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Configuration'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailProviderConfig;
